import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { NebulizerChartRow } from "../../../../../../utils/chartUtils";
import { getLongFormattedDate } from "../../../../../../utils/dateFormatter";
import {
  HeaderContainer,
  Subtitle,
  Title,
} from "../../MeasurementModal.styles";
import {
  DataContainer,
  Name,
  Row,
  TreatmentContainer,
} from "./TreatmentSection.styles";

type TreatmentSectionProps = {
  treatment: NebulizerChartRow;
};

export function TreatmentSection({ treatment }: TreatmentSectionProps) {
  const { t } = useTranslation();

  const handleData = () => {
    if (treatment.isAverage) {
      return (
        <>
          <Row>
            <Name>
              {t("patientPage_measurementModal_treatment_treatments")}
            </Name>
            <p>{treatment.treatmentsCount}</p>
          </Row>
          <Row>
            <Name>
              {t("patientPage_measurementModal_treatment_avgDuration")}
            </Name>
            <p>{`${treatment.totalTreatmentTimeNum} (sec)`}</p>
          </Row>
          <Row>
            <Name>
              {t("patientPage_measurementModal_treatment_avgInhaling")}
            </Name>
            <p>{`${treatment.avgInhalingTimeNum} (sec)`}</p>
          </Row>
        </>
      );
    }

    return (
      <>
        <Row>
          <Name>
            {t("patientPage_measurementModal_treatment_timeOfTreatment")}
          </Name>
          <p>{dayjs(treatment.time).format("HH:mm:ss")}</p>
        </Row>
        <Row>
          <Name>{t("patientPage_measurementModal_treatment_duration")}</Name>
          <p>{`${treatment.totalTreatmentTime} (sec)`}</p>
        </Row>
        <Row>
          <Name>
            {t("patientPage_measurementModal_treatment_totalInhalation")}
          </Name>
          <p>{`${treatment.totalTimeInhaling} (sec)`}</p>
        </Row>
        <Row>
          <Name>
            {t("patientPage_measurementModal_treatment_totalNebulization")}
          </Name>
          <p>{`${treatment.totalNebulizationTime} (sec)`}</p>
        </Row>
        <Row>
          <Name>
            {t("patientPage_measurementModal_treatment_avgInhalation")}
          </Name>
          <p>{`${treatment.meanInspirationTime} (sec)`}</p>
        </Row>
        <Row>
          <Name>
            {t("patientPage_measurementModal_treatment_avgInhalationFlow")}
          </Name>
          <p>{`${treatment.meanInhalationFlow} (slm)`}</p>
        </Row>
        <Row>
          <Name>
            {t("patientPage_measurementModal_treatment_avgBreathingCycle")}
          </Name>
          <p>{`${treatment.meanCycleTime} (sec)`}</p>
        </Row>
        <Row>
          <Name>{t("patientPage_measurementModal_treatment_avgPeakFlow")}</Name>
          <p>{`${treatment.meanPeakFlow} (slm)`}</p>
        </Row>
        <Row>
          <Name>
            {t("patientPage_measurementModal_treatment_cartridgeTime")}
          </Name>
          <p>
            {t("patientPage_measurementModal_treatment_days", {
              count: treatment.chamberAgeDays,
            })}
          </p>
        </Row>
        <Row>
          <Name>
            {t("patientPage_measurementModal_treatment_cartridgeSerial")}
          </Name>
          <p>{`${treatment.chamberId}`}</p>
        </Row>
      </>
    );
  };

  return (
    <TreatmentContainer>
      <HeaderContainer css={{ marginLeft: 0, marginBottom: 38 }}>
        <Title>
          {treatment.isAverage
            ? t("patientPage_measurementModal_treatment_monthlySummary")
            : t("patientPage_measurementModal_treatment_selectedTreatment")}
        </Title>
        <Subtitle css={{ marginTop: 0, marginBottom: 0 }}>
          {treatment.isAverage
            ? treatment.monthRangeLabel
            : getLongFormattedDate(treatment.time)}
        </Subtitle>
      </HeaderContainer>

      <DataContainer>{handleData()}</DataContainer>
    </TreatmentContainer>
  );
}
